import React, { useCallback } from 'react';
import { cn } from '../../utils/cn';
import { useAssetPreviewStore } from '../../store/assetPreviewStore';
import { useProjectPanelStore } from '../../store/projectPanelStore';
import { useAssetUrl } from '../../hooks/useAssetUrl';
import { useAudioPlayer } from '../../hooks/useAudioPlayer';
import {
  Play,
  Pause,
  Square,
  Repeat,
  Volume2,
  VolumeX,
  Music,
} from 'lucide-react';

interface AudioPreviewProps {
  instanceId: string;
}

const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2];

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export const AudioPreview: React.FC<AudioPreviewProps> = ({ instanceId }) => {
  const { previews } = useAssetPreviewStore();
  const { assets } = useProjectPanelStore();

  const instance = previews[instanceId];
  const asset = instance ? assets[instance.assetId] : null;

  const { url: audioUrl, loading, error } = useAssetUrl(instance?.assetPath || null);

  const {
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    isLooping,
    playbackRate,
    isReady,
    togglePlay,
    stop,
    seekPercent,
    setVolume,
    toggleMute,
    toggleLoop,
    setPlaybackRate,
  } = useAudioPlayer({
    instanceId,
    audioUrl,
  });

  const handleProgressClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percent = (x / rect.width) * 100;
      seekPercent(percent);
    },
    [seekPercent]
  );

  const handleVolumeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setVolume(parseFloat(e.target.value));
    },
    [setVolume]
  );

  const handlePlaybackRateChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setPlaybackRate(parseFloat(e.target.value));
    },
    [setPlaybackRate]
  );

  if (!instance || !asset) {
    return (
      <div className="flex-1 flex items-center justify-center text-zinc-500">
        Asset not found
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-zinc-700 animate-pulse mb-4" />
        <div className="text-zinc-500 text-sm">Loading audio...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center p-4">
        <div className="text-4xl mb-2">⚠️</div>
        <div className="text-zinc-400 text-sm">Failed to load audio</div>
        <div className="text-zinc-600 text-xs mt-1">{error.message}</div>
      </div>
    );
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="flex-1 flex flex-col p-4">
      {/* Visualization area */}
      <div className="flex-1 flex items-center justify-center mb-4">
        <div
          className={cn(
            'w-32 h-32 rounded-full',
            'bg-gradient-to-br from-purple-600 to-pink-600',
            'flex items-center justify-center',
            'shadow-lg shadow-purple-500/20',
            isPlaying && 'animate-pulse'
          )}
        >
          <Music size={48} className="text-white/80" />
        </div>
      </div>

      {/* File name */}
      <div className="text-center mb-4">
        <div className="text-zinc-200 font-medium truncate">{asset.name}</div>
        <div className="text-zinc-500 text-xs uppercase">{asset.type}</div>
      </div>

      {/* Progress bar */}
      <div className="mb-4">
        <div
          className="h-2 bg-zinc-700 rounded-full cursor-pointer overflow-hidden"
          onClick={handleProgressClick}
        >
          <div
            className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-75"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-zinc-500 mt-1">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>

      {/* Main controls */}
      <div className="flex items-center justify-center gap-2 mb-4">
        {/* Stop button */}
        <button
          className={cn(
            'p-2 rounded-full',
            'bg-zinc-700 hover:bg-zinc-600',
            'text-zinc-300 hover:text-white',
            'transition-colors',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
          onClick={stop}
          disabled={!isReady}
          title="Stop"
        >
          <Square size={18} />
        </button>

        {/* Play/Pause button */}
        <button
          className={cn(
            'p-4 rounded-full',
            'bg-gradient-to-br from-purple-600 to-pink-600',
            'hover:from-purple-500 hover:to-pink-500',
            'text-white',
            'transition-all',
            'shadow-lg shadow-purple-500/30',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
          onClick={togglePlay}
          disabled={!isReady}
          title={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? <Pause size={24} /> : <Play size={24} className="ml-0.5" />}
        </button>

        {/* Loop button */}
        <button
          className={cn(
            'p-2 rounded-full',
            isLooping ? 'bg-purple-600 text-white' : 'bg-zinc-700 text-zinc-400',
            'hover:bg-zinc-600 hover:text-white',
            'transition-colors',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
          onClick={toggleLoop}
          disabled={!isReady}
          title={isLooping ? 'Loop: On' : 'Loop: Off'}
        >
          <Repeat size={18} />
        </button>
      </div>

      {/* Secondary controls */}
      <div className="flex items-center gap-4">
        {/* Volume control */}
        <div className="flex items-center gap-2 flex-1">
          <button
            className={cn(
              'p-1 rounded',
              'text-zinc-400 hover:text-white',
              'transition-colors'
            )}
            onClick={toggleMute}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted || volume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            className="flex-1 h-1 bg-zinc-700 rounded-full appearance-none cursor-pointer
                       [&::-webkit-slider-thumb]:appearance-none
                       [&::-webkit-slider-thumb]:w-3
                       [&::-webkit-slider-thumb]:h-3
                       [&::-webkit-slider-thumb]:rounded-full
                       [&::-webkit-slider-thumb]:bg-white
                       [&::-webkit-slider-thumb]:cursor-pointer"
          />
          <span className="text-xs text-zinc-500 w-8 text-right">
            {Math.round((isMuted ? 0 : volume) * 100)}%
          </span>
        </div>

        {/* Playback speed */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-500">Speed:</span>
          <select
            value={playbackRate}
            onChange={handlePlaybackRateChange}
            className={cn(
              'bg-zinc-700 text-zinc-300 text-xs',
              'border border-zinc-600 rounded',
              'px-2 py-1',
              'cursor-pointer',
              'focus:outline-none focus:ring-1 focus:ring-purple-500'
            )}
            disabled={!isReady}
          >
            {PLAYBACK_RATES.map((rate) => (
              <option key={rate} value={rate}>
                {rate}x
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};
